<script>
// ConversationView.vue
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
  },


watch: {
  conversationId() {
    this.fetchConversationIfUnavailable();
  },

  // Monitora mudanças no assignee_id da conversa atual
  'currentChat.assignee_id': {
    handler(newAssigneeId, oldAssigneeId) {
      if (this.conversationId && newAssigneeId !== oldAssigneeId) {
        this.checkConversationAccess();
      }
    },
    immediate: false,
  },
},

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
  // MÉTODOS ORIGINAIS
  onConversationLoad() {
    this.fetchConversationIfUnavailable();
  },
  
  initialize() {
    this.$store.dispatch('setActiveInbox', this.inboxId);
    this.setActiveChat();
  },
  
  toggleConversationLayout() {
    const { LAYOUT_TYPES } = wootConstants;
    const {
      conversation_display_type:
        conversationDisplayType = LAYOUT_TYPES.CONDENSED,
    } = this.uiSettings;
    const newViewType =
      conversationDisplayType === LAYOUT_TYPES.CONDENSED
        ? LAYOUT_TYPES.EXPANDED
        : LAYOUT_TYPES.CONDENSED;
    this.updateUISettings({
      conversation_display_type: newViewType,
      previously_used_conversation_display_type: newViewType,
    });
  },
  
  fetchConversationIfUnavailable() {
    if (!this.conversationId) {
      return;
    }
    const chat = this.findConversation();
    if (!chat) {
      this.$store.dispatch('getConversation', this.conversationId);
    }
  },
  
  findConversation() {
    const conversationId = parseInt(this.conversationId, 10);
    const [chat] = this.chatList.filter(c => c.id === conversationId);
    return chat;
  },
  
  setActiveChat() {
    if (this.conversationId) {
      const selectedConversation = this.findConversation();
      if (
        !selectedConversation ||
        selectedConversation.id === this.currentChat.id
      ) {
        return;
      }
      const { messageId } = this.$route.query;
      this.$store
        .dispatch('setActiveChat', {
          data: selectedConversation,
          after: messageId,
        })
        .then(() => {
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
        });
    } else {
      this.$store.dispatch('clearSelectedState');
    }
  },
  
  onSearch() {
    this.showSearchModal = true;
  },
  
  closeSearch() {
    this.showSearchModal = false;
  },

  // CONTROLE DE ACESSO - Verifica se conversa está na lista filtrada pelo backend
  canAccessConversation(conversation) {
    if (!conversation?.id) {
      return false;
    }
    // Confia no backend - se a conversa está no chatList, o usuário tem acesso
    return this.chatList.some(c => c.id === conversation.id);
  },

  checkConversationAccess() {
    if (!this.currentChat.id) {
      return;
    }

    // Só verifica se o chatList já foi carregado
    if (this.chatList.length === 0) {
      return;
    }

    if (!this.canAccessConversation(this.currentChat)) {
      this.redirectToInbox();
    }
  },

  redirectToInbox() {
    const { accountId } = this;
    this.$store.dispatch('clearSelectedState');
    this.$router.push({ name: 'home' }).catch(() => {
      this.$router.push({ path: `/app/accounts/${accountId}/conversations` }).catch(() => {});
    });
  },
},

};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
    <CmdBarConversationSnooze />
  </section>
</template>
